import React, { useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { Box, Flex, HStack, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { formatAmount } from 'common/formatAmount';
import webView from 'common/webView';
import Accordion from 'components/_core/Accordion';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { formatPeriodDate } from 'features/Accounting/pages/Estimation/common/utils';
import TaxStatus from 'features/Accounting/pages/Estimation/components/TaxStatus';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';
import VatInput from 'features/Work/components/VatInput';

import StepLayout from '../../layout/Step';
import InputField from '../RevenueSituation/components/InputField';
import RevenueHeader from '../RevenueSituation/components/RevenueHeader';
import { StepComponentProps } from '../types';
import { locales } from './locales';
import { VatSituationSchema, vatSituationSchema } from './vatSituation.schema';

type SavePayload = {
  vatRate: number;
  vatOffset: number | null;
};

const VatSituation: React.FC<StepComponentProps<SavePayload>> = ({
  context,
  onBack,
  onNext,
  onSave,
}) => {
  const { isInWebView } = webView;
  const { showToast } = useToast();
  const { stepProgress, taxForecastPeriod } = context;
  const revenueTotal =
    (taxForecastPeriod?.revenue || 0) +
    (taxForecastPeriod?.complementaryRevenue || 0);
  const vatAmount = taxForecastPeriod?.vatAmount || 0;
  // Use the period's vatRate if it exists, otherwise calculate it from the vatAmount
  const defaultVatRate = taxForecastPeriod?.vatRate
    ? taxForecastPeriod.vatRate * 100
    : parseFloat(((vatAmount / revenueTotal) * 100).toFixed(2)) || 0;

  const [updateTaxData, { loading: updateTaxDataLoading }] = useMutation(
    UpdateTaxDataDocument,
    { onError: () => showToast({ message: locales.error, type: 'error' }) },
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<VatSituationSchema>({
    defaultValues: {
      vatOffset: taxForecastPeriod?.vatOffset || 0,
      vatRate: defaultVatRate,
    },
    resolver: zodResolver(vatSituationSchema),
  });

  const [vatOffset, vatRate] = watch(['vatOffset', 'vatRate']);

  const formattedVatAmount = useMemo<number>(() => {
    const withoutTaxesAmount = revenueTotal / (1 + vatRate / 100);
    const vat = revenueTotal - withoutTaxesAmount;
    const total = vat + (vatOffset || 0);

    return parseFloat(total.toFixed(2));
  }, [revenueTotal, vatOffset, vatRate]);

  const withoutVatFormatted = useMemo<string>(() => {
    const total = revenueTotal - formattedVatAmount;

    return formatAmount({ forcePlusSign: false, value: total });
  }, [formattedVatAmount, revenueTotal]);

  const onSubmit: SubmitHandler<VatSituationSchema> = async (data) => {
    const input: SavePayload = {
      vatOffset: data.vatOffset,
      vatRate: data.vatRate / 100,
    };

    await updateTaxData({
      onCompleted: () => {
        onSave(input);
        onNext();
      },
      variables: {
        input: {
          companyProfileId: context.companyProfile.companyProfileId,
          taxForecastPeriodData: {
            taxForecastPeriodId: taxForecastPeriod?.taxForecastPeriodId,
            vatOffset: input.vatOffset,
            vatRate: input.vatRate,
          },
          taxForecastProfileData: {
            vatDefaultRate: input.vatRate,
          },
          taxFrequency: context.companyProfile.taxFrequency,
        },
      },
    });
  };

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body padding={isInWebView ? 'space-0' : 'space-16'}>
        <Box paddingX={isInWebView ? 'space-16' : undefined}>
          <Stack>
            <Heading size="lg">{locales.title}</Heading>

            {!isInWebView ? <Text size="lg">{locales.subtitle}</Text> : null}
          </Stack>

          <HStack align="center" gap="space-8" marginY="space-24">
            <Text fontWeight="weight-500" variant="primary">
              {formatPeriodDate(
                taxForecastPeriod?.periodStartsAt ?? 0,
                taxForecastPeriod?.periodEndsAt ?? 0,
              )}
            </Text>

            <TaxStatus
              isPaid={taxForecastPeriod?.isPaid ?? false}
              payBefore={taxForecastPeriod?.payBefore ?? 0}
            />
          </HStack>

          <RevenueHeader
            focusVatAmount={true}
            isSubjectToVat={true}
            revenue={formatAmount({
              forcePlusSign: false,
              value: revenueTotal,
            })}
            revenueWithoutVat={withoutVatFormatted}
          />
        </Box>
        {isInWebView ? (
          <Box backgroundColor="grey.300" height="space-6" marginY="space-32" />
        ) : null}

        <Box paddingX={isInWebView ? 'space-16' : undefined}>
          <HStack
            alignItems={isInWebView ? 'start' : 'center'}
            flexDirection={isInWebView ? 'column' : 'row'}
            justifyContent="space-between"
            marginBottom="space-24"
          >
            <Text fontSize="small" variant="secondary">
              {locales.form.label.vatRate}
            </Text>
            <Controller
              control={control}
              name="vatRate"
              render={({ field, fieldState }) => (
                <Form.Field
                  error={fieldState.error?.message}
                  marginRight="space-24"
                  width={isInWebView ? '100%' : undefined}
                >
                  <VatInput
                    onChange={(value) =>
                      field.onChange(value === '' ? value : Number(value))
                    }
                    value={field.value}
                  />
                </Form.Field>
              )}
            />
          </HStack>
          <HStack
            alignItems={isInWebView ? 'start' : 'center'}
            flexDirection={isInWebView ? 'column' : 'row'}
            gap="space-4"
            justifyContent="space-between"
            marginBottom="space-24"
          >
            <Text fontSize="small" variant="secondary">
              {locales.form.label.vatAmount}
            </Text>
            <Flex>
              <Text
                fontWeight="weight-500"
                marginRight={isInWebView ? 'space-2' : 'space-20'}
                variant="primary"
              >
                {formattedVatAmount}
              </Text>
              <Text
                fontWeight="weight-500"
                variant="primary"
                whiteSpace="nowrap"
              >
                {locales.currency}
              </Text>
            </Flex>
          </HStack>
          <InputField
            displayTax={false}
            error={errors?.vatOffset?.message}
            inputProps={register('vatOffset', {
              valueAsNumber: true,
            })}
            label={locales.form.label.vatOffset}
            placeholder="+/- 0"
          />
        </Box>
      </StepLayout.Body>

      <StepLayout.Sidebar>
        <Heading marginBottom="space-16">{locales.faq.title}</Heading>

        <Accordion allowMultiple allowToggle={undefined}>
          <Accordion.Item title={locales.faq.question1.title}>
            <Accordion.Text>{locales.faq.question1.subtitle}</Accordion.Text>
          </Accordion.Item>
        </Accordion>
      </StepLayout.Sidebar>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isLoading={updateTaxDataLoading}
          onClick={handleSubmit(onSubmit)}
        />
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default VatSituation;
